import React from 'react';

import { CustomInfoAlertMessage, ModalFactory } from '@Components';
import { AppDispatch } from '../../../../../store.type';
import {
  EVENT_ROUTE_KEYS,
  EventButtonType,
  EventNamesV2,
  FLOW_TYPE
} from '@Utils/events/eventsConstants';
import { BENE_ADDRESS_INFO_DATA } from '../../../../bene-additional-info/utils/constants';
import { logEventV2 } from '@Utils/events/logUserEvents';
import { activateLoader, disableLoader } from '@Redux/actions/ui-trigger';
import { isEmpty } from 'lodash';
import { encryptDataWithSignature, removeKeys } from '@Utils/library';
import {
  arrowTagPattern,
  BASE_URL,
  CORPORATE,
  INDIVIDUAL,
  INITIATED,
  REGISTERED
} from '@Utils/constants';
import {
  payeeDetailsSubmitRequest,
  payeeDetailsSubmitRequestNew,
  sendMoneyPatch
} from '@Redux/actions/individual-user-flow/individual-user-flow-action';
import { storeRecentAddedBene } from '@Redux/actions/transaction/transaction-action';
import { VALID_ADDRESS_FIELDS } from '@Utils/templates/address';
import { showMessage } from '@Redux/actions/common-message/message-action';
import { updateScreenConfig } from '@Redux/actions/user-flow';
import { PAYEE_PARTIAL_EDIT_FILTER } from '../../../../payee-details/payee_partial_edit_filter';
import {
  navigateHistory,
  setBeneBankNameAction,
  setCurrentFormBeneAddressAction,
  toggleShouldPrefillBeneAddressAction
} from '@Redux/actions/new-bene-flow/new-bene-flow-action';
import { GenericRecordType } from '../../personal-details/utils/types';
import { store } from '../../../../../store';
import { screenNames } from '../../../constants';

const handlePrimaryCTAClick = (
  data: GenericRecordType,
  dispatch: AppDispatch,
  onClose: (flag: boolean) => void,
  additionalData: Record<string, any> = {}
) => {
  const { currentFormBeneAddress } = additionalData;

  const currentScreen = store.getState().rootReducer.newBeneficiaryReducer.currentScreen;
  dispatch(setCurrentFormBeneAddressAction(currentFormBeneAddress));

  dispatch(toggleShouldPrefillBeneAddressAction(true));
  if (data?.isNameVerificationError) {
    if (currentScreen === screenNames.recipient_address.name) {
      // Go back 2 screens to personal details screen
      dispatch(navigateHistory({ type: 'POP_NAVIGATION_HISTORY' }));
      dispatch(navigateHistory({ type: 'POP_NAVIGATION_HISTORY' }));
    } else dispatch(navigateHistory({ type: 'POP_NAVIGATION_HISTORY' }));
  } else if (data?.isAccountVerificationError) {
    if (currentScreen === screenNames.recipient_address.name) {
      dispatch(navigateHistory({ type: 'POP_NAVIGATION_HISTORY' }));
    }
  }
  onClose(true);
};

const triggerBeneficiaryErrorModal = async (
  data: any,
  values: any,
  dispatch: AppDispatch,
  isBeneSchemaApplicable: boolean = false,
  additionalData: Record<string, any> = {},
  isRedirectedFromSendMoney: boolean = false,
  isAddressVisible: boolean = false,
  beneficiaryData: any[] = []
) => {
  const validationError = data?.isAccountVerificationError
    ? 'bypassAccountVerification'
    : 'bypassNameVerification';

  //@ts-ignore
  return await ModalFactory.builder(
    (onClose: (flag: boolean) => void) => {
      return (
        <CustomInfoAlertMessage
          contentType="componentMessageCentreItem"
          contentId={
            data?.isAccountVerificationError
              ? 'BENE_API_ERROR'
              : data?.data?.displayBeneName
              ? 'BENE_API_BANK_NAME_MINOR_ERROR'
              : 'BENE_API_BANK_NAME_ERROR'
          }
          primaryCtaOnClick={() => handlePrimaryCTAClick(data, dispatch, onClose, additionalData)}
          beneValidationObject={
            data?.isNameVerificationError
              ? ''
              : values.bene_schema_validation || values.bank_card_validations
          }
          values={values}
          shouldShowShareCTAs={true}
          secondaryCtaOnClick={() => {
            onClose(true);
            handleBeneSubmit(
              values,
              isBeneSchemaApplicable,
              validationError,
              dispatch,
              '',
              additionalData,
              isRedirectedFromSendMoney,
              isAddressVisible,
              beneficiaryData
            );
          }}
          bankNameInvalid={data?.isNameVerificationError}
          beneficiaryBankName={data?.data?.beneficiaryBankName}
          displayBeneName={data?.data?.displayBeneName}
          //@ts-ignore
          shareMessage={null}
        />
      );
    },
    'insta-modal center-modal center',
    true
  );
};

const submitBeneSchemaEnabled = async (
  values: GenericRecordType,
  validationError: string,
  dispatch: AppDispatch,
  formID: string,
  isRedirectedFromSendMoney: boolean = false,
  additionalData: Record<string, any> = {}
) => {
  const { is_partially_editable, is_completely_editable } = window.history.state?.usr || {};
  const { beneId = null, shouldUpdateCountry, bankAccCountryToUse } = additionalData;
  const processedValues = { ...values };
  if (shouldUpdateCountry) {
    processedValues.updated_country_code = bankAccCountryToUse;
  }
  if (validationError === 'bypassAccountVerification') {
    processedValues.bypass_account_verification = true;
  }
  if (validationError === 'bypassNameVerification') {
    processedValues.bypass_name_verification = true;
  }

  let encryptField;
  if (processedValues.bene_schema_validation) {
    Object.keys(processedValues.bene_schema_validation).forEach((field) => {
      const encryption =
        processedValues?.bene_schema_validation[field]?.render?.api_validation?.encryption;
      if (!isEmpty(encryption)) encryptField = encryption;
    });
  }
  if (isEmpty(processedValues.routing_config_type)) {
    processedValues.routing_config_type = processedValues.routing_config_type_temp;
    delete processedValues.routing_config_type_temp;
  }
  delete processedValues.bene_schema_validation;

  if (encryptField && !processedValues[encryptField + '_disabled']) {
    const encryptedCardData = encryptDataWithSignature(processedValues[encryptField]);
    processedValues[encryptField] = encryptedCardData.encryptedData;
    processedValues.signature = encryptedCardData.signature;

    delete processedValues.card_token;
  } else if (processedValues.card_number && processedValues[encryptField + '_disabled']) {
    delete processedValues.card_number;
  }

  Object.keys(processedValues).forEach((field) => {
    if (arrowTagPattern.test(processedValues[field])) {
      processedValues[field] = processedValues[field].split(arrowTagPattern)[0];
    }
    if (field.endsWith('_success') || field.endsWith('_disabled')) delete processedValues[field];
  });
  dispatch(activateLoader());
  const response = await dispatch(
    payeeDetailsSubmitRequestNew(
      processedValues,
      beneId,
      inferIfIndependent(additionalData.screens),
      { is_partially_editable, is_completely_editable },
      {
        formID
      }
    )
  );

  if (
    !response.success &&
    (response.isAccountVerificationError || response.isNameVerificationError)
  ) {
    if (response.isNameVerificationError)
      dispatch(setBeneBankNameAction(response.data?.beneficiaryBankName));
    await triggerBeneficiaryErrorModal(
      response,
      processedValues,
      dispatch,
      true,
      additionalData,
      isRedirectedFromSendMoney
    );
    dispatch(disableLoader());
    return;
  }

  if (response?.data?.success) {
    const {
      details_selected,
      currency_code,
      beneficiary_bank_address_info,
      bank_account_country,
      account_type
    } = values;
    const country_code = beneficiary_bank_address_info?.country_code || bank_account_country;

    const { isEditFlow } = additionalData || {};
    const eventProperty = {
      receivingMethod: details_selected,
      currency: currency_code,
      recipientCountry: country_code,
      recipientType: account_type
    };

    const eventType = isEditFlow ? EventNamesV2.RECIPIENT_UPDATED : EventNamesV2.RECIPIENT_ADDED;
    logEventV2(eventType, eventProperty);

    // save the bene_id from response to re-order bene_list in bene dropdown on send_money screen
    dispatch(storeRecentAddedBene(response.data.data));

    dispatch(navigateHistory({ type: 'PUSH_NAVIGATION_HISTORY', screen: 'recipient_added' }));
    dispatch(disableLoader());
    return response.data;
  }
  dispatch(disableLoader());
  return { success: false };
};

const submitNonSchemaBeneficiary = async (
  props: any,
  validationError: string,
  dispatch: AppDispatch,
  formID: string,
  isRedirectedFromSendMoney: boolean,
  isAddressVisible: boolean,
  additionalData: Record<string, any> = {},
  beneficiaryData: any[] = []
) => {
  try {
    const isIndividualChinaBeneWithCNYCurrency =
      props.bank_account_country === 'CN' &&
      props.currency_code === 'CNY' &&
      props.account_type === INDIVIDUAL;

    if (props.country_code === 'SG' && isAddressVisible) {
      let { block_number, street_number } = props;
      if (!block_number && !street_number)
        return dispatch(
          showMessage({
            text: ['Please enter either block or street number'],
            type: 'info'
          })
        );
    }

    dispatch(activateLoader());
    if (!isAddressVisible) {
      removeKeys(props, VALID_ADDRESS_FIELDS);
    }

    const { debit_config_id, cash_payout_config, wallet_config, config_id } =
      additionalData.bankDetails || {};
    const { beneId = null, isEditFlow, shouldUpdateCountry, bankAccCountryToUse } = additionalData;
    if (shouldUpdateCountry) {
      props.updated_country_code = bankAccCountryToUse;
    }
    const { is_partially_editable, is_completely_editable } = window.history.state?.usr || {};
    let { details_selected, is_card_number_updated } = props;
    details_selected = details_selected?.toLowerCase();
    let route_config_id =
      details_selected === 'cash' || details_selected === 'money_cash'
        ? cash_payout_config
        : details_selected === 'card'
        ? debit_config_id
        : details_selected === 'wallet'
        ? wallet_config
        : config_id;

    if (
      (details_selected === 'card' || isIndividualChinaBeneWithCNYCurrency) &&
      props.card_number
    ) {
      const encryptedCardData = encryptDataWithSignature(props.card_number);
      props.card_number = encryptedCardData.encryptedData;
      props.signature = encryptedCardData.signature;
    }
    if (isEditFlow && details_selected === 'card') {
      delete props.card_token;
    }
    if (beneId && details_selected === 'card') {
      props.is_card_number_updated = is_card_number_updated;
    }
    if (validationError === 'bypassAccountVerification') {
      props.bypass_account_verification = true;
    }
    if (validationError === 'bypassNameVerification') {
      props.bypass_name_verification = true;
    }
    let propsCpy = { ...props, account_type: props.account_type.toUpperCase() };
    if (
      props.bank_account_country !== 'CN' &&
      props.currency_code !== 'CNY' &&
      additionalData.userAccountType !== CORPORATE
    ) {
      delete props.beneficiary_identification_type;
      delete props.beneficiary_identification_value;
    }

    const payeeDetailsData =
      is_partially_editable && !is_completely_editable
        ? //@ts-ignore
          filterPayload(props, PAYEE_PARTIAL_EDIT_FILTER)
        : propsCpy;

    const fullName = payeeDetailsData['beneficiary_name'] || payeeDetailsData['full_name'];

    if (fullName) {
      const nameParts = fullName.trim().split(' ');
      payeeDetailsData['first_name'] = nameParts[0];
      payeeDetailsData['middle_name'] =
        nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '';
      payeeDetailsData['last_name'] = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
    }

    delete payeeDetailsData['beneficiary_name'];
    delete payeeDetailsData['beneficiary_country_code'];
    delete payeeDetailsData['is_mailing_address_diff'];
    delete payeeDetailsData['residential'];
    delete payeeDetailsData['mailing'];
    delete payeeDetailsData['fullAddress'];
    delete payeeDetailsData['streetNumber'];
    delete payeeDetailsData['route'];
    delete payeeDetailsData['bank_account_number_success'];
    delete payeeDetailsData['full_name']; //We are sending first_name and last_name

    const response = await dispatch(
      payeeDetailsSubmitRequest(
        payeeDetailsData,
        route_config_id,
        beneId,
        inferIfIndependent(additionalData.screens),
        { is_partially_editable, is_completely_editable },
        {
          formID
        }
      )
    );

    if (
      !response.success &&
      (response.isAccountVerificationError || response.isNameVerificationError)
    ) {
      await triggerBeneficiaryErrorModal(
        response,
        props,
        dispatch,
        false,
        additionalData,
        isRedirectedFromSendMoney,
        isAddressVisible,
        beneficiaryData
      );
      dispatch(disableLoader());
      return;
    }

    if (response?.data?.success) {
      const {
        details_selected,
        currency_code,
        beneficiary_bank_address_info,
        bank_account_country,
        account_type
      } = payeeDetailsData;
      const country_code = beneficiary_bank_address_info?.country_code || bank_account_country;
      const { isEditFlow } = additionalData || {};

      const eventProperty = {
        receivingMethod: details_selected,
        currency: currency_code,
        recipientCountry: country_code,
        recipientType: account_type
      };

      const eventType = isEditFlow ? EventNamesV2.RECIPIENT_UPDATED : EventNamesV2.RECIPIENT_ADDED;
      logEventV2(eventType, eventProperty);
      // save the bene_id from response to re-order bene_list in bene dropdown on send_money screen
      dispatch(storeRecentAddedBene(response.data.data));

      const { user_session, userStatusReducer, transactionDetails } = additionalData;

      if (
        userStatusReducer &&
        (userStatusReducer.status === REGISTERED || userStatusReducer.status === INITIATED) &&
        user_session &&
        user_session.country_code === 'US' &&
        response.data.data.beneficiary_id &&
        transactionDetails &&
        !transactionDetails.beneficiary_id
      ) {
        dispatch(sendMoneyPatch(transactionDetails, response.data.data.beneficiary_id));
      }

      if (inferIfIndependent(additionalData.screens))
        dispatch(updateScreenConfig('add_beneficiary', { isIndependent: false }));

      if (
        userStatusReducer &&
        userStatusReducer.status === 'ACKNOWLEDGED' &&
        userStatusReducer.country_code === 'SG'
      ) {
        additionalData.history.push(`${BASE_URL}/amaze-acknowledge`);
      } else if (additionalData.redirectUrl) {
        return additionalData.history.push(additionalData.redirectUrl);
      }
      dispatch(navigateHistory({ type: 'PUSH_NAVIGATION_HISTORY', screen: 'recipient_added' }));
    } else {
      dispatch(disableLoader());
    }
    dispatch(disableLoader());
    return response.data;
  } catch (e) {
    dispatch(disableLoader());
    // eslint-disable-next-line no-console
    console.error(e);
    return { success: false };
  } finally {
    dispatch(disableLoader());
  }
};

const inferIfIndependent = (screens: any) => {
  if (!screens) return false;
  let screen = screens.find((screen: any) => screen.name === 'add_beneficiary');
  // eslint-disable-next-line no-prototype-builtins
  return (screen && screen.hasOwnProperty('isIndependent') && screen.isIndependent) || false;
};

export const handleBeneSubmit = async (
  values: GenericRecordType,
  isBeneSchemaEnabled: boolean,
  validationError: string,
  dispatch: AppDispatch,
  formID: string,
  additionalData: Record<string, any> = {},
  isRedirectedFromSendMoney: boolean = false,
  isAddressVisible: boolean = false,
  beneficiaryData: any[] = []
) => {
  const eventProperty = {
    type: EventButtonType.BUTTON,
    pageName: EVENT_ROUTE_KEYS.AddBeneficiary,
    ctaName: BENE_ADDRESS_INFO_DATA.saveAndContinue,
    flowType: FLOW_TYPE.BENEFICIARY
  };
  logEventV2(EventNamesV2.CTA_CLICKED, eventProperty);

  if (isBeneSchemaEnabled) {
    return await submitBeneSchemaEnabled(
      values,
      validationError,
      dispatch,
      formID,
      isRedirectedFromSendMoney,
      additionalData
    );
  } else {
    return await submitNonSchemaBeneficiary(
      values,
      validationError,
      dispatch,
      formID,
      isRedirectedFromSendMoney,
      isAddressVisible,
      additionalData,
      beneficiaryData
    );
  }
};

const filterPayload = (
  payload: Record<string, any>,
  filterObj: Record<string, any>
): Record<string, any> => {
  const reqPayload: Record<string, any> = {};

  Object.keys(payload).forEach((key) => {
    if (key in filterObj) {
      reqPayload[key] = payload[key];
    }
  });
  return reqPayload;
};
